import { useEffect, useState } from "react";
import { useData } from "../../context/DataContext";
import { sfx } from "../../lib/sound";

const DOTS = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0];

export function CountdownDots({ running, duration, done }: { running: boolean; duration: number; done: boolean }) {
  const { prefs, fastMode } = useData();
  const [step, setStep] = useState(done ? 0 : 11);
  useEffect(() => {
    if (!running) {
      setStep(done ? 0 : 11);
      return;
    }
    if (fastMode) return setStep(0);
    setStep(10);
    const every = duration / 11;
    const t = setInterval(() => setStep((s) => {
      if (prefs.sound_effects && s > 0) sfx.tick(s <= 3);
      return Math.max(0, s - 1);
    }), every);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, done]);

  return (
    <div className="text-center" data-testid="countdown-dots">
      <p className="mb-1.5 font-comic text-sm uppercase tracking-wide text-white/90">{done ? "Decided!" : "Deciding in…"}</p>
      {!done && <div className="flex justify-center gap-1 sm:gap-1.5">
        {DOTS.map((n) => {
          const lit = step <= n;
          const current = step === n && running && !done;
          return (
            <span key={n} className={`flex h-6 w-6 items-center justify-center rounded-full border-2 font-comic text-[11px] transition-colors duration-75 sm:h-7 sm:w-7 sm:text-xs ${
              n === 0 ? "border-[#FFB000] bg-black text-white" : lit ? "border-[#FFC15E] bg-[#FF5D1F] text-white" : "border-white/20 bg-white/10 text-white/50"} ${current ? "scale-125" : ""}`}>
              {n}
            </span>
          );
        })}
      </div>}
    </div>
  );
}

export function StageHeader({ title }: { title: string }) {
  return (
    <div className="text-center">
      <p className="font-comic text-base text-[#FFB000] sm:text-lg">≈ DITHER ≈</p>
      <p className="font-comic text-sm uppercase text-white sm:text-base [overflow-wrap:anywhere]">{title}</p>
    </div>
  );
}

export const resultClass = (s: string) =>
  s.length <= 6 ? "text-6xl sm:text-7xl" : s.length <= 12 ? "text-5xl sm:text-6xl" : s.length <= 24 ? "text-4xl sm:text-5xl" : s.length <= 50 ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl";

export function SaysResult({ result, detail, testId = "says-result" }: { result: string; detail?: string; testId?: string }) {
  return (
    <div className="relative text-center" data-testid={testId}>
      <span className="ua-highlight text-lg sm:text-xl" data-testid="result-reveal-heading">DITHER says…</span>
      <div className="relative mt-2 flex items-center justify-center gap-2">
        <span className="hidden shrink-0 flex-col gap-1.5 sm:flex" aria-hidden>
          <i className="block h-1.5 w-6 -rotate-[25deg] rounded bg-[#FF5D1F]" /><i className="block h-1.5 w-8 rounded bg-[#FF8A2B]" /><i className="block h-1.5 w-6 rotate-[25deg] rounded bg-[#FF5D1F]" />
        </span>
        <h2 className={`ua-logo px-1 py-1 [overflow-wrap:anywhere] ${resultClass(result)}`} data-testid="result-reveal-value">{result}</h2>
        <span className="hidden shrink-0 flex-col gap-1.5 sm:flex" aria-hidden>
          <i className="block h-1.5 w-6 rotate-[25deg] rounded bg-[#FF5D1F]" /><i className="block h-1.5 w-8 rounded bg-[#FF8A2B]" /><i className="block h-1.5 w-6 -rotate-[25deg] rounded bg-[#FF5D1F]" />
        </span>
      </div>
      {detail && <p className="mt-1 text-sm font-bold text-[#6b3a12]" data-testid="result-reveal-detail">{detail}</p>}
    </div>
  );
}
